import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { Icon } from '../Icon';
import { useStore } from '@store/StoreProvider';

export const TrialSection: React.FC = observer(() => {
  const ringRef = useRef<SVGCircleElement>(null);
  const { auth, i18n } = useStore();
  const isLoggedIn = auth.isAuthenticated;
  void i18n.language;
  void i18n.version;

  useEffect(() => {
    const ring = ringRef.current;
    if (!ring) return;
    if (!('IntersectionObserver' in window)) {
      ring.classList.add('on');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('on');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(ring);
    return () => io.disconnect();
  }, []);

  const checks: { bold: string; rest?: string; off?: boolean }[] = [
    { bold: '3 оператора', rest: 'на всё время триала' },
    { bold: '3 канала связи', rest: 'сайт и Telegram' },
    { bold: 'До 5 регионов', rest: 'на каждый канал' },
    { bold: 'Шаблоны 6 + 6', rest: 'общих и личных' },
    { bold: 'Расширенная кастомизация' },
    { bold: 'Бренд ChatX в виджете остаётся', off: true },
  ];

  return (
    <section id="trial" className="relative pt-8 sm:pt-10 pb-14 sm:pb-20 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="reveal relative rounded-3xl overflow-hidden border border-emerald-500/25 bg-gradient-to-br from-emerald-500/8 via-panel/50 to-brand/8 p-7 sm:p-10">
          <div className="trial-glow pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full" />
          <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] font-bold bg-emerald-500/12 text-emerald-400 border border-emerald-500/25 mb-4">
                <span className="relative w-1.5 h-1.5 rounded-full bg-current">
                  <span className="absolute inset-0 rounded-full bg-current animate-ping" />
                </span>
                {i18n.t('billing.trial.title')}
              </div>
              <h2 className="text-2xl sm:text-4xl font-extrabold leading-tight">
                7 дней, <span className="text-gradient">3 оператора</span>, карта не нужна
              </h2>
              <p className="mt-3 text-slate-400 max-w-xl">
                Триал включается сразу при регистрации. Можно собрать команду, подключить каналы и
                провести настоящие диалоги — не демо.
              </p>

              <div className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl">
                {checks.map((c) => (
                  <div key={c.bold} className="flex gap-2.5 text-sm">
                    <Icon
                      id={c.off ? 'ic-minus' : 'ic-check'}
                      className={`ic ic-sm shrink-0 mt-0.5 ${c.off ? 'text-slate-500' : 'text-emerald-400'}`}
                    />
                    <span className={c.off ? 'text-slate-400' : undefined}>
                      {c.off ? (
                        c.bold
                      ) : (
                        <>
                          <b className="text-white">{c.bold}</b>
                          {c.rest ? <span className="text-slate-400"> {c.rest}</span> : null}
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <p className="mt-6 text-[13px] text-slate-500 max-w-xl">
                По окончании приём новых сообщений приостанавливается — панель и вся история остаются
                доступны. Оплатили тариф, и всё продолжает работать с того же места.
              </p>
            </div>

            <div className="lg:w-[268px] flex flex-col gap-4">
              <div className="rounded-2xl border border-white/10 bg-deep/45 p-5">
                <div className="flex items-center gap-4">
                  <div className="relative shrink-0 grid place-items-center w-[74px] h-[74px]">
                    <svg viewBox="0 0 76 76" className="absolute inset-0 w-full h-full -rotate-90">
                      <circle cx="38" cy="38" r="33" fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="5" />
                      <circle
                        ref={ringRef}
                        className="trial-ring"
                        cx="38"
                        cy="38"
                        r="33"
                        fill="none"
                        stroke="url(#trialGrad)"
                        strokeWidth="5"
                        strokeLinecap="round"
                      />
                      <defs>
                        <linearGradient id="trialGrad" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0" stopColor="#34d399" />
                          <stop offset="1" stopColor="#3b82f6" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="relative text-center leading-none">
                      <div className="text-[22px] font-extrabold">7</div>
                      <div className="text-[9.5px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                        дней
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12px] font-bold text-slate-300 mb-2">Команда</div>
                    <div className="flex items-center">
                      <span className="op-av">А</span>
                      <span className="op-av">М</span>
                      <span className="op-av">К</span>
                      <span className="ml-2.5 text-[11.5px] text-slate-500 font-semibold">3 из 3</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-3 text-center">
                  <div>
                    <div className="text-lg font-extrabold">3</div>
                    <div className="text-[10.5px] text-slate-500 leading-tight">канала связи</div>
                  </div>
                  <div>
                    <div className="text-lg font-extrabold">5</div>
                    <div className="text-[10.5px] text-slate-500 leading-tight">регионов на канал</div>
                  </div>
                </div>
              </div>

              {isLoggedIn ? (
                <Link
                  to="/myProfile"
                  className="text-center py-4 rounded-2xl bg-brand hover:bg-blue-600 font-bold shadow-lg shadow-brand/30 transition active:scale-95"
                >
                  {i18n.t('nav.to_profile')}
                </Link>
              ) : (
                <a
                  href="/register"
                  className="text-center py-4 rounded-2xl bg-brand hover:bg-blue-600 font-bold shadow-lg shadow-brand/30 transition active:scale-95"
                >
                  Начать 7 дней
                </a>
              )}
              <a
                href="#pricing"
                className="text-center py-3.5 rounded-2xl border border-white/15 font-semibold hover:bg-white/5 transition"
              >
                {i18n.t('billing.plans.view')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});
